import { useEffect, useState } from "react";
import { Brain, Loader2, Plus, Trash2, CheckCircle2, XCircle, RefreshCw, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";

export default function LLMVisibilityPage() {
  const { active } = useProjects();
  const [queries, setQueries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [terms, setTerms] = useState("");
  const [running, setRunning] = useState(null); // query id being checked
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [history, setHistory] = useState({}); // { queryId: [...] }

  const refresh = async () => {
    if (!active) return;
    const [{ data: q }, { data: s }] = await Promise.all([
      api.get(`/llm-visibility/queries?project_id=${active.id}`),
      api.get(`/llm-visibility/summary?project_id=${active.id}`),
    ]);
    setQueries(q); setSummary(s);
  };

  useEffect(() => { refresh(); }, [active?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/llm-visibility/queries", {
        project_id: active.id,
        prompt,
        brand_terms: terms.split(",").map((t) => t.trim()).filter(Boolean),
      });
      setOpen(false); setPrompt(""); setTerms("");
      refresh();
    } catch (e2) { setError(formatApiError(e2)); }
  };

  const run = async (q) => {
    setRunning(q.id);
    try {
      await api.post(`/llm-visibility/queries/${q.id}/check`);
      await refresh();
    } catch {}
    finally { setRunning(null); }
  };

  const remove = async (id) => {
    await api.delete(`/llm-visibility/queries/${id}`).catch(() => {});
    refresh();
  };

  const loadHistory = async (id) => {
    const { data } = await api.get(`/llm-visibility/queries/${id}/checks`);
    setHistory((h) => ({ ...h, [id]: data }));
  };

  const toggleExpand = (id) => {
    if (expanded === id) {
      setExpanded(null);
    } else {
      setExpanded(id);
      if (!history[id]) loadHistory(id);
    }
  };

  if (!active) {
    return (
      <DashboardLayout eyebrow="LLM Visibility" title="Get cited by AI." subtitle="Track how often ChatGPT mentions your brand for relevant queries.">
        <div className="bg-zinc-950 border border-dashed border-zinc-800 rounded-sm p-16 text-center" data-testid="llm-no-project">
          <Brain className="w-10 h-10 mx-auto mb-4 text-zinc-700" />
          <h3 className="font-display text-xl mb-2">No project selected</h3>
          <p className="text-zinc-400 mb-6">Create or activate a project to track AI search visibility.</p>
          <Button asChild className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm">
            <Link to="/onboarding">Run onboarding <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      eyebrow="LLM Visibility"
      title="Are you mentioned in ChatGPT answers?"
      subtitle="Define real user queries. We ask GPT-5.2 and detect whether your brand is recommended."
      action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-10" data-testid="llm-add-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Track query
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display">Track a new query</DialogTitle>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div>
                <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">User prompt</Label>
                <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} required rows={3}
                          placeholder="e.g. What's the best AI SEO tool for SaaS founders?"
                          className="bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="llm-prompt-input" />
              </div>
              <div>
                <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Brand terms (comma-separated, optional)</Label>
                <Input value={terms} onChange={(e) => setTerms(e.target.value)}
                       placeholder={`${active.name}, ${active.domain}`}
                       className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="llm-terms-input" />
                <p className="text-xs text-zinc-600 mt-1.5">Defaults to your project name + domain if empty.</p>
              </div>
              {error && <div className="text-sm text-red-400" data-testid="llm-error">{error}</div>}
              <DialogFooter>
                <Button type="submit" className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="llm-create-submit">Track</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <SummaryCard label="Tracked queries" value={summary?.total_queries ?? 0} testid="llm-summary-queries" />
        <SummaryCard label="Total checks" value={summary?.total_checks ?? 0} testid="llm-summary-checks" />
        <SummaryCard label="Mention rate" value={`${summary?.mention_rate_pct ?? 0}%`} accent={(summary?.mention_rate_pct ?? 0) >= 30 ? "#10B981" : (summary?.mention_rate_pct ?? 0) >= 10 ? "#F59E0B" : "#EF4444"} testid="llm-summary-rate" />
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5 col-span-2 lg:col-span-1">
          <span className="label-eyebrow">Trend</span>
          <div className="h-12 mt-2">
            {(summary?.series?.length ?? 0) > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={summary.series}>
                  <Line type="monotone" dataKey="mentions" stroke="#0055FF" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-xs text-zinc-600 mt-2">Run checks to see history</div>
            )}
          </div>
        </div>
      </div>

      {/* Query list */}
      {queries.length === 0 ? (
        <div className="bg-zinc-950 border border-dashed border-zinc-800 rounded-sm p-16 text-center" data-testid="llm-empty">
          <Brain className="w-10 h-10 mx-auto mb-4 text-zinc-700" />
          <h3 className="font-display text-xl mb-2">No tracked queries</h3>
          <p className="text-zinc-400">Add a real user prompt to see if AI search recommends your brand.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="llm-queries">
          {queries.map((q) => (
            <div key={q.id} className="bg-zinc-950 border border-zinc-800 rounded-sm" data-testid={`llm-q-${q.id}`}>
              <div className="px-6 py-4 flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {q.last_check?.any_mention ? (
                      <Badge className="bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30 rounded-sm">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Mentioned
                      </Badge>
                    ) : q.last_check ? (
                      <Badge className="bg-[#EF4444]/15 text-[#EF4444] border-[#EF4444]/30 rounded-sm">
                        <XCircle className="w-3 h-3 mr-1" /> Not mentioned
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-500">Not checked</Badge>
                    )}
                    {q.brand_terms.map((t) => (
                      <Badge key={t} variant="outline" className="rounded-sm border-zinc-800 text-zinc-500 text-[10px]">{t}</Badge>
                    ))}
                  </div>
                  <p className="text-zinc-200 leading-relaxed">{q.prompt}</p>
                  {q.last_check && (
                    <p className="text-xs text-zinc-500 mt-1.5">Last check {new Date(q.last_check.checked_at).toLocaleString()}</p>
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <Button onClick={() => run(q)} size="sm" disabled={running === q.id}
                          className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-8 text-xs"
                          data-testid={`llm-run-${q.id}`}>
                    {running === q.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><RefreshCw className="w-3 h-3 mr-1" /> Run check</>}
                  </Button>
                  <Button onClick={() => toggleExpand(q.id)} size="sm" variant="outline"
                          className="bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm h-8 text-xs"
                          data-testid={`llm-expand-${q.id}`}>
                    {expanded === q.id ? "Hide" : "History"}
                  </Button>
                  <Button onClick={() => remove(q.id)} size="sm" variant="ghost"
                          className="text-zinc-500 hover:text-[#EF4444] hover:bg-zinc-900 rounded-sm h-8"
                          data-testid={`llm-delete-${q.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              {expanded === q.id && (
                <div className="border-t border-zinc-800 bg-[#09090B]" data-testid={`llm-history-${q.id}`}>
                  {(history[q.id] || []).length === 0 ? (
                    <div className="px-6 py-6 text-sm text-zinc-500 text-center">No checks yet for this query.</div>
                  ) : (
                    <ul className="divide-y divide-zinc-900">
                      {(history[q.id] || []).map((c) => (
                        <li key={c.id} className="px-6 py-4">
                          <div className="flex items-center gap-2 mb-2">
                            {c.any_mention ? (
                              <Badge className="bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30 rounded-sm text-[10px]">MENTIONED</Badge>
                            ) : (
                              <Badge className="bg-[#EF4444]/15 text-[#EF4444] border-[#EF4444]/30 rounded-sm text-[10px]">NOT MENTIONED</Badge>
                            )}
                            <span className="text-xs text-zinc-500 font-mono">{new Date(c.checked_at).toLocaleString()}</span>
                          </div>
                          <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{c.response}</p>
                          {c.mentions?.length > 0 && (
                            <div className="mt-2 flex gap-2 flex-wrap">
                              {c.mentions.map((m) => (
                                <Badge key={m.term} variant="outline" className="rounded-sm border-zinc-800 text-zinc-500 text-[10px]">
                                  {m.term} · {m.count}×
                                </Badge>
                              ))}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}

function SummaryCard({ label, value, accent, testid }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5" data-testid={testid}>
      <span className="label-eyebrow">{label}</span>
      <div className="font-display text-3xl font-semibold tracking-tighter mt-2" style={{ color: accent || "#F8FAFC" }}>
        {value}
      </div>
    </div>
  );
}
